import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated blog posts list
 */
export default class BlogPostsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleLinkClick);
    window.addEventListener('popstate', this.#handlePopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleLinkClick);
    window.removeEventListener('popstate', this.#handlePopState);
  }

  #handleLinkClick = async (e) => {
    // Intercept clicks on category pills or pagination links
    const link = e.target.closest('.category-pill, .pagination__link, .pagination a');
    if (!link) return;

    // Do not intercept if it's a external link or has target="_blank"
    if (link.getAttribute('target') === '_blank') return;

    e.preventDefault();
    const url = new URL(link.href);
    await this.loadCategory(url);
  };

  #handlePopState = async () => {
    // When user clicks back/forward, update the layout via AJAX matching the new URL
    const url = new URL(window.location.href);
    await this.loadCategory(url, false);
  };

  async loadCategory(url, pushState = true) {
    const gridWrapper = this.querySelector('[ref="gridWrapper"]');
    
    // Fade out for visual feedback
    if (gridWrapper) {
      gridWrapper.style.transition = 'opacity 0.2s ease';
      gridWrapper.style.opacity = '0.4';
    }

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');

      const htmlText = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlText, 'text/html');

      const newGridWrapper = doc.querySelector('[ref="gridWrapper"]');
      const newPaginationWrapper = doc.querySelector('.blog-posts-pagination');
      const newHero = doc.querySelector('.editorial-hero');
      const newFeatured = doc.querySelector('.featured-story');
      const newFilters = doc.querySelector('.category-filters');

      // Update URL first
      if (pushState) {
        history.pushState('', '', url.toString());
      }

      // Smooth View Transitions if supported, fallback to immediate update
      if (document.startViewTransition) {
        document.startViewTransition(() => {
          this.#updateDOM(newGridWrapper, newPaginationWrapper, newHero, newFeatured, newFilters);
        });
      } else {
        this.#updateDOM(newGridWrapper, newPaginationWrapper, newHero, newFeatured, newFilters);
      }
    } catch (error) {
      console.error('AJAX blog list navigation failed:', error);
      // Fallback to normal navigation
      window.location.href = url.toString();
    }
  }

  #updateDOM(newGridWrapper, newPaginationWrapper, newHero, newFeatured, newFilters) {
    // 1. Replace the Grid content
    const currentGridWrapper = this.querySelector('[ref="gridWrapper"]');
    if (currentGridWrapper && newGridWrapper) {
      currentGridWrapper.replaceWith(newGridWrapper);
    }

    // 2. Replace the Pagination controls
    const currentPaginationWrapper = this.querySelector('.blog-posts-pagination');
    if (currentPaginationWrapper && newPaginationWrapper) {
      currentPaginationWrapper.replaceWith(newPaginationWrapper);
    } else if (currentPaginationWrapper && !newPaginationWrapper) {
      currentPaginationWrapper.innerHTML = '';
    }

    // 3. Update active pill filters
    const currentFilters = this.querySelector('.category-filters');
    if (currentFilters && newFilters) {
      currentFilters.replaceWith(newFilters);
    }

    // 4. Update Editorial Hero visibility / content
    const currentHero = this.querySelector('.editorial-hero');
    if (currentHero && newHero) {
      currentHero.replaceWith(newHero);
    } else if (currentHero && !newHero) {
      currentHero.remove();
    } else if (!currentHero && newHero) {
      // Prepend before the filters wrapper
      const filtersWrapper = this.querySelector('.category-filters-wrapper');
      if (filtersWrapper) {
        filtersWrapper.before(newHero);
      }
    }

    // 5. Update Featured Story visibility / content
    const currentFeatured = this.querySelector('.featured-story');
    if (currentFeatured && newFeatured) {
      currentFeatured.replaceWith(newFeatured);
    } else if (currentFeatured && !newFeatured) {
      currentFeatured.remove();
    } else if (!currentFeatured && newFeatured) {
      const filtersWrapper = this.querySelector('.category-filters-wrapper');
      if (filtersWrapper) {
        filtersWrapper.before(newFeatured);
      }
    }

    // 6. Clear PaginatedList cache so that pagination pages refetch correctly for the new tags
    if (this.pages && typeof this.pages.clear === 'function') {
      this.pages.clear();
    }
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
